import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';

import { TitleList } from '@/lazy-load-components';

import { CollectionType } from '@/enums/collection-type';
import type TitleListGraphqlType from '@/components/list/TitleList.vue';

import { useFilterStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useQuery } from '@/helpers/composables/useApollo';

import {
	GetSearchTitlesDocument,
	GetSearchTitlesQuery,
	GetSearchTitlesQueryVariables,
} from '@/pages/graphql/queries/GetSearchTitles.query';
import { PopularTitlesSorting, TitleFilter } from '@/@types/graphql-types';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { TitleListLayout } from '@/enums/title-list-layout';
import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { toastError } from '@/helpers/toast/toast-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { TitleEventPayloadGraphql, trackTitleEventGraphql } from '@/helpers/title-helper';
import ProfileView from '../ProfileView.vue';
import { useUserProfile } from '../../composables/useUserProfile';


export default /*#__PURE__*/_defineComponent({
  __name: 'UserTitles',
  props: {
    personContentId: null
  },
  setup(__props: any) {

const props = __props as { personContentId: string };

const { isLoggedIn } = useUserStore();
const { language, country } = useLanguageStore();
const { filters, selectedProviders } = useFilterStore();
const { profile } = useUserProfile();

const isFirstPageLoaded = ref(false);
const listLayout = ref<TitleListLayout>(TitleListLayout.ROW);
const isFiltersVisible = ref(false);
const showFooter = ref(false);
const titleList = ref<InstanceType<typeof TitleListGraphqlType> | null>(null);



const filtersSearch = computed(() => filters.value(CollectionType.SEARCH));

const graphqlFilters = computed<TitleFilter>(() =>
	toGraphqlFilters(filtersSearch.value, selectedProviders.value(!isLoggedIn.value))
);

const personId = computed(() => `tp${props.personContentId}`);

const queryVariables = computed<GetSearchTitlesQueryVariables>(() => ({
	searchAfterCursor: '',
	searchTitlesFilter: {
		...(isFiltersVisible.value || isLoggedIn.value ? graphqlFilters.value : {}),
		personId: personId.value ?? null,
		includeTitlesWithoutUrl: true,
	},
	searchTitlesSortBy: PopularTitlesSorting.Popular,
	language: language.value,
	country: country.value,
	location: 'UserTitles',
}));

const {
	result: searchPageData,
	loading: isTitlesLoading,
	fetchMore,
	onError,
	onResult,
} = useQuery<GetSearchTitlesQuery, GetSearchTitlesQueryVariables>(GetSearchTitlesDocument, queryVariables);

onResult(() => (isFirstPageLoaded.value = true));

onError(error => {
	captureMessageForSentry('[GraphQL Get Search Page Data]:', { error, where: 'Component: Search' }, 'error');
	if (process.client) showErrorToast();
});

function onLoadNext({ event }: { event: any }) {
	if (event == null) return;

	const searchTitlesFetchMore = fetchMore({
		variables: {
			searchAfterCursor: searchPageData.value?.searchTitles.pageInfo.endCursor,
		},
		updateQuery(previousResult, { fetchMoreResult }) {
			if (!fetchMoreResult) return previousResult;

			const previousEdges = previousResult.searchTitles.edges ?? [];
			const fetchMoreEdges = fetchMoreResult.searchTitles.edges ?? [];

			return {
				searchTitles: {
					__typename: previousResult?.searchTitles.__typename,
					totalCount: fetchMoreResult?.searchTitles.totalCount,
					edges: [...previousEdges, ...fetchMoreEdges],
					pageInfo: fetchMoreResult?.searchTitles.pageInfo!,
				},
			};
		},
	});

	if (searchTitlesFetchMore == null) return;
	searchTitlesFetchMore
		.catch(error => {
			captureMessageForSentry(
				'[GraphQL Search Page Load More Titles Issue]:',
				{ error, where: 'Component: Search, method: onLoadNext' },
				'error'
			);
		})
		.finally(() => event.target?.complete?.(searchTitles.value.length));
}

const searchTitles = computed(() => searchPageData.value?.searchTitles.edges ?? []);

const titleAmount = computed(() => searchPageData.value?.searchTitles.totalCount ?? 0);

const isFirstPage = computed(() => !searchPageData.value?.searchTitles.pageInfo?.hasPreviousPage);

const isAtEndOfList = computed(() => !searchPageData.value?.searchTitles.pageInfo?.hasNextPage);

function trackClick(payload: TitleEventPayloadGraphql) {
	trackTitleEventGraphql('title_clicked', payload, 'searchsuggester');
}

const { tc } = useI18n();
function showErrorToast() {
	toastError({ message: tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
}

return { __sfc: true,isLoggedIn, language, country, filters, selectedProviders, profile, isFirstPageLoaded, listLayout, isFiltersVisible, showFooter, titleList, props, filtersSearch, graphqlFilters, personId, queryVariables, searchPageData, isTitlesLoading, fetchMore, onError, onResult, onLoadNext, searchTitles, titleAmount, isFirstPage, isAtEndOfList, trackClick, tc, showErrorToast, TitleList, CollectionType, ProfileView }
}

})